import { IconPlus, IconTrash } from "@tabler/icons-react";
import type { FC } from "react";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { IconButton } from "../Buttons/iconButton.js";
import { Size } from "../helpers/helpers.js";
import { Input } from "../Inputs/input.js";
import { Label } from "../Label/label.js";

export type KeyValuePair = {
  key: string;
  value: string;
};

export interface KeyValuePairListProps {
  label: string;
  description: string;
  value: readonly KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  addButtonLabel: string;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  lengthOptions?: {
    keyMinLength?: number;
    keyMaxLength?: number;
    valueMinLength?: number;
    valueMaxLength?: number;
  };
}

export const KeyValuePairList: FC<KeyValuePairListProps> = ({
  label,
  description,
  value,
  onChange,
  addButtonLabel,
  keyPlaceholder = "Key",
  valuePlaceholder = "Value",
  lengthOptions,
}) => {
  const handleAddPair = (): void => {
    onChange([...value, { key: "", value: "" }]);
  };

  const handleRemovePair = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleUpdatePair = (
    index: number,
    field: "key" | "value",
    newValue: string,
  ): void => {
    const updated = [...value];
    const existing = updated[index];
    if (existing === undefined) return;
    updated[index] = { ...existing, [field]: newValue };
    onChange(updated);
  };

  return (
    <div className="flex flex-col space-y-2">
      <Label size={Size.SMALL}>{label}</Label>
      <p className="text-sm text-muted-foreground">{description}</p>

      {value.map((pair, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              placeholder={keyPlaceholder}
              value={pair.key}
              onChange={(e) => handleUpdatePair(index, "key", e.target.value)}
              minLength={lengthOptions?.keyMinLength}
              maxLength={lengthOptions?.keyMaxLength}
            />
          </div>
          <div className="flex-1">
            <Input
              placeholder={valuePlaceholder}
              value={pair.value}
              onChange={(e) => handleUpdatePair(index, "value", e.target.value)}
              minLength={lengthOptions?.valueMinLength}
              maxLength={lengthOptions?.valueMaxLength}
            />
          </div>
          <IconButton
            icon={<IconTrash className="h-4 w-4" />}
            variant={ButtonVariant.Tertiary}
            onClick={() => handleRemovePair(index)}
            accessibleLabel={`Remove ${pair.key || "item"}`}
          />
        </div>
      ))}

      <div>
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.MEDIUM}
          icon={<IconPlus className="h-4 w-4" />}
          onClick={handleAddPair}
        >
          {addButtonLabel}
        </Button>
      </div>
    </div>
  );
};
